import { Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteAlert from "../../../components/Alert/DeleteAlert";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import NavigationDrawer from "../../../components/NavigationDrawer";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import {
  deleteAdminFaq,
  gatAdminFaqByUserType,
} from "../../../services/adminFaqApiFn";
import { handleOpenSnackBar } from "../../../utils";
import AddEditFaq from "./AddEditFaq";
import TabBar from "./TabBar";

const headerData = ["question", "answer", "actions"];

const tableRows = ["faq_question_both", "faq_answer_both", "actions"];

function AllPlans() {
  const faqDialogRef = useRef(null);
  const openDeleteDialog = useRef(null);
  const { t } = useTranslation(["faq", "common"]);
  const [allFaqsByType, setAllFaqsByType] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [role, setRole] = useState("worker");

  const fetchAllPlans = async (role) => {
    try {
      setIsLoading(true);
      const response = await gatAdminFaqByUserType(role);
      setIsLoading(false);
      if (response.data.status === "success") {
        setAllFaqsByType(response.data.data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPlans("worker");
  }, []);

  const handleChange = useCallback((_, newValue) => {
    setRole(newValue);

    switch (newValue) {
      case "company":
        fetchAllPlans("company");
        break;
      case "supplier":
        fetchAllPlans("supplier");
        break;
      case "education":
        fetchAllPlans("education");
        break;
      case "user":
        fetchAllPlans("user");
        break;
      case "plan":
        fetchAllPlans("plan");
        break;
      case "plan":
        fetchAllPlans("plan");
        break;
      case "package":
        fetchAllPlans("package");
        break;
      case "gift-card":
        fetchAllPlans("gift-card");
        break;
      case "event":
        fetchAllPlans("event");
        break;
      case "user-account":
        fetchAllPlans("user-account");
        break;
      default:
        fetchAllPlans("worker");
    }
  }, []);

  const handleOpenUpdateDialog = async (params) => {
    faqDialogRef.current.openDialog({
      data: params.row,
      userType: role,
      type: "edit",
      getData: fetchAllPlans,
    });
  };

  const handleOpenDeleteDialog = (params) => {
    openDeleteDialog.current.openDialog({
      data: params.row,
      warningText: t("deleteFaqConfirmation"),
    });
  };

  const handleDelete = async (params) => {
    try {
      setIsLoading(true);
      const response = await deleteAdminFaq(params.data._id);
      if (response.data.status == "success") {
        openDeleteDialog.current.closeDialog();
        fetchAllPlans(role);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        direction={"row"}
        gap={2}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          styleProps={{
            height: 40,
          }}
          handleClickBtn={() => {
            faqDialogRef.current.openDialog({
              userType: role,
              type: "add",
              getData: fetchAllPlans,
            });
          }}
        />
      </Stack>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("allFaqs")}
        subtitle={t("faqDescription")}
        headerActions={renderHeaderAction()}
      >
        <TabBar value={role} handleChange={handleChange} />
        <br />
        <DynamicPaginatedTable
          showPagination={true}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allFaqsByType}
          showActions
          showEdit
          showDelete
          handleEdit={handleOpenUpdateDialog}
          handleDelete={handleOpenDeleteDialog}
        />
        <AddEditFaq ref={faqDialogRef} />
        <DeleteAlert ref={openDeleteDialog} handleDelete={handleDelete} />
      </PaperContainer>
    </NavigationDrawer>
  );
}

AllPlans.propTypes = {
  plansData: PropTypes.array,
};

export default AllPlans;
